:host {
  /**
   * @prop --background: Background of the button
   * @prop --background-activated: Background of the button when pressed. Note: setting this will interfere with the Material Design ripple.
   * @prop --background-activated-opacity: Opacity of the button when pressed
   * @prop --background-focused: Background of the button when focused with the tab key
   * @prop --background-focused-opacity: Opacity of the button when focused with the tab key
   * @prop --background-hover: Background of the button on hover
   * @prop --background-hover-opacity: Opacity of the background on hover
   *
   * @prop --color: Text color of the button
   * @prop --color-activated: Text color of the button when pressed
   * @prop --color-focused: Text color of the button when focused with the tab key
   * @prop --color-hover: Text color of the button when hover
   *
   * @prop --transition: Transition of the button
   *
   * @prop --border-radius: Border radius of the button
   * @prop --border-width: Border width of the button
   * @prop --border-style: Border style of the button
   * @prop --border-color: Border color of the button
   *
   * @prop --ripple-color: Color of the button ripple effect
   *
   * @prop --box-shadow: Box shadow of the button
   * @prop --opacity: Opacity of the button
   *
   * @prop --padding-top: Top padding of the button
   * @prop --padding-end: Right padding if direction is left-to-right, and left padding if direction is right-to-left of the button
   * @prop --padding-bottom: Bottom padding of the button
   * @prop --padding-start: Left padding if direction is left-to-right, and right padding if direction is right-to-left of the button
   */
  --overflow: hidden;
  --ripple-color: currentColor;
  --border-width: initial;
  --border-color: initial;
  --border-style: initial;
  --color-activated: var(--color);
  --color-focused: var(--color);
  --color-hover: var(--color);
  --box-shadow: none;
  display: inline-block;
  width: auto;
  color: var(--color);
  font-family: var(--ion-font-family, inherit);
  text-align: center;
  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  vertical-align: top;
  vertical-align: -webkit-baseline-middle;
  font-kerning: none;
}

:host(.button-disabled) {
  cursor: default;
  opacity: 0.5;
  pointer-events: none;
}

:host(.button-solid) {
  --background: var(--ion-color-primary, #3880ff);
  --color: var(--ion-color-primary-contrast, #fff);
}

:host(.button-outline) {
  --border-color: var(--ion-color-primary, #3880ff);
  --background: transparent;
  --color: var(--ion-color-primary, #3880ff);
}

:host(.button-clear) {
  --border-width: 0;
  --background: transparent;
  --color: var(--ion-color-primary, #3880ff);
}

:host(.button-block) {
  display: block;
}

:host(.button-block) .button-native {
  margin-left: 0;
  margin-right: 0;
  width: 100%;
  clear: both;
  contain: content;
}

:host(.button-block) .button-native::after {
  clear: both;
}

:host(.button-full) {
  display: block;
}

:host(.button-full) .button-native {
  margin-left: 0;
  margin-right: 0;
  width: 100%;
  contain: content;
}

:host(.button-full:not(.button-round)) .button-native {
  border-radius: 0;
  border-right-width: 0;
  border-left-width: 0;
}

.button-native {
  border-radius: var(--border-radius);
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  margin-bottom: 0;
  -webkit-padding-start: var(--padding-start);
  padding-inline-start: var(--padding-start);
  -webkit-padding-end: var(--padding-end);
  padding-inline-end: var(--padding-end);
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);
  font-family: inherit;
  font-size: inherit;
  font-style: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  text-decoration: inherit;
  text-indent: inherit;
  text-overflow: inherit;
  text-transform: inherit;
  text-align: inherit;
  white-space: inherit;
  color: inherit;
  display: flex;
  position: relative;
  align-items: center;
  width: 100%;
  height: 100%;
  min-height: inherit;
  transition: var(--transition);
  border-width: var(--border-width);
  border-style: var(--border-style);
  border-color: var(--border-color);
  outline: none;
  background: var(--background);
  line-height: 1;
  box-shadow: var(--box-shadow);
  contain: layout style;
  cursor: pointer;
  opacity: var(--opacity);
  overflow: var(--overflow);
  z-index: 0;
  box-sizing: border-box;
  appearance: none;
}

.button-native::-moz-focus-inner {
  border: 0;
}

.button-inner {
  display: flex;
  position: relative;
  flex-flow: row nowrap;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  z-index: 1;
}

::slotted([slot=start]),
::slotted([slot=end]) {
  flex-shrink: 0;
}

::slotted(ion-icon) {
  font-size: 1.35em;
  pointer-events: none;
}

::slotted(ion-icon[slot=start]) {
  -webkit-margin-start: -0.3em;
  margin-inline-start: -0.3em;
  -webkit-margin-end: 0.3em;
  margin-inline-end: 0.3em;
  margin-top: 0;
  margin-bottom: 0;
}

::slotted(ion-icon[slot=end]) {
  -webkit-margin-start: 0.3em;
  margin-inline-start: 0.3em;
  -webkit-margin-end: -0.2em;
  margin-inline-end: -0.2em;
  margin-top: 0;
  margin-bottom: 0;
}

::slotted(ion-icon[slot=icon-only]) {
  font-size: 1.8em;
}

ion-ripple-effect {
  color: var(--ripple-color);
}

.button-native::after {
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  position: absolute;
  content: "";
  opacity: 0;
}

:host(.ion-focused) {
  color: var(--color-focused);
}

:host(.ion-focused) .button-native::after {
  background: var(--background-focused);
  opacity: var(--background-focused-opacity);
}

@media (any-hover: hover) {
  :host(:hover) {
    color: var(--color-hover);
  }
  :host(:hover) .button-native::after {
    background: var(--background-hover);
    opacity: var(--background-hover-opacity);
  }
}
:host(.ion-activated) {
  color: var(--color-activated);
}

:host(.ion-activated) .button-native::after {
  background: var(--background-activated);
  opacity: var(--background-activated-opacity);
}

:host(.button-solid.ion-color) .button-native {
  background: var(--ion-color-base);
  color: var(--ion-color-contrast);
}

:host(.button-outline.ion-color) .button-native {
  border-color: var(--ion-color-base);
  background: transparent;
  color: var(--ion-color-base);
}

:host(.button-clear.ion-color) .button-native {
  background: transparent;
  color: var(--ion-color-base);
}

:host(.in-toolbar:not(.ion-color):not(.in-toolbar-color)) .button-native {
  color: var(--ion-toolbar-color, var(--color));
}

:host(.button-outline.in-toolbar:not(.ion-color):not(.in-toolbar-color)) .button-native {
  border-color: var(--ion-toolbar-color, var(--color, var(--border-color)));
}

:host(.button-solid.in-toolbar:not(.ion-color):not(.in-toolbar-color)) .button-native {
  background: var(--ion-toolbar-color, var(--background));
  color: var(--ion-toolbar-background, var(--color));
}

:host(.button-outline.ion-activated.in-toolbar:not(.ion-color):not(.in-toolbar-color)) .button-native {
  background: var(--ion-toolbar-color, var(--color));
  color: var(--ion-toolbar-background, var(--background), var(--ion-color-primary-contrast, #fff));
}

:host {
  --border-radius: 4px;
  --padding-top: 8px;
  --padding-bottom: 8px;
  --padding-start: 1.1em;
  --padding-end: 1.1em;
  --transition: box-shadow 280ms cubic-bezier(.4, 0, .2, 1),
                background-color 15ms linear,
                color 15ms linear;
  -webkit-margin-start: 2px;
  margin-inline-start: 2px;
  -webkit-margin-end: 2px;
  margin-inline-end: 2px;
  margin-top: 4px;
  margin-bottom: 4px;
  min-height: 36px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

:host(.button-solid) {
  --background-activated: transparent;
  --background-hover: var(--ion-color-primary-contrast, #fff);
  --background-focused: var(--ion-color-primary-contrast, #fff);
  --background-activated-opacity: 0;
  --background-focused-opacity: .24;
  --background-hover-opacity: .08;
  --box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
}

:host(.button-solid.ion-activated) {
  --box-shadow: 0 5px 5px -3px rgba(0, 0, 0, 0.2), 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 3px 14px 2px rgba(0, 0, 0, 0.12);
}

:host(.button-outline) {
  --border-width: 2px;
  --border-style: solid;
  --box-shadow: none;
  --background-activated: transparent;
  --background-focused: var(--ion-color-primary, #3880ff);
  --background-hover: var(--ion-color-primary, #3880ff);
  --background-activated-opacity: 0;
  --background-focused-opacity: .12;
  --background-hover-opacity: .04;
}

:host(.button-outline.ion-activated.ion-color) .button-native {
  background: transparent;
}

:host(.button-clear) {
  --background-activated: transparent;
  --background-focused: var(--ion-color-primary, #3880ff);
  --background-hover: var(--ion-color-primary, #3880ff);
  --background-activated-opacity: 0;
  --background-focused-opacity: .12;
  --background-hover-opacity: .04;
}

:host(.button-round) {
  --border-radius: 64px;
  --padding-top: 0;
  --padding-start: 26px;
  --padding-end: 26px;
  --padding-bottom: 0;
}

:host(.button-large) {
  --padding-top: 14px;
  --padding-start: 1em;
  --padding-end: 1em;
  --padding-bottom: 14px;
  min-height: 2.8em;
  font-size: 20px;
}

:host(.button-small) {
  --padding-top: 4px;
  --padding-start: 0.9em;
  --padding-end: 0.9em;
  --padding-bottom: 4px;
  min-height: 2.1em;
  font-size: 13px;
}

:host(.button-has-icon-only) {
  --padding-top: 0;
  --padding-bottom: 0;
}

:host(.button-strong) {
  font-weight: bold;
}

::slotted(ion-icon[slot=icon-only]) {
  padding-left: 0;
  padding-right: 0;
  padding-top: 0;
  padding-bottom: 0;
}

/**
 * Only allow overriding of opacity here
 * as developers should not be overriding
 * colors when using the color prop.
 */
:host(.button-solid.ion-color.ion-focused) .button-native::after {
  background: var(--ion-color-contrast);
}

:host(.button-clear.ion-color.ion-focused) .button-native::after,
:host(.button-outline.ion-color.ion-focused) .button-native::after {
  background: var(--ion-color-base);
}

@media (any-hover: hover) {
  :host(.button-solid.ion-color:hover) .button-native::after {
    background: var(--ion-color-contrast);
  }
  :host(.button-clear.ion-color:hover) .button-native::after,
  :host(.button-outline.ion-color:hover) .button-native::after {
    background: var(--ion-color-base);
  }
}